import React from 'react'
import PropTypes from 'prop-types'

import { R } from '@utils/currency'
import { Card, CardHeading, CardContent } from '@components/layout/Card'

import { MonthlyRetirementBudgetBucket } from './MonthlyRetirementBudgetBucket'

const formatNumber = number => R(number).format()

const EditedMonthlyRetirementBudget = ({
  baseScenarioDesiredRetirementIncome,
  baseScenarioActualRetirementIncome,
  baseScenarioRetirementIncomeAbsoluteDifference,
  baseScenarioRetirementIncomeDifferenceText,

  comparedScenarioDesiredRetirementIncome,
  comparedScenarioActualRetirementIncome,
  comparedScenarioRetirementIncomeAbsoluteDifference,
  comparedScenarioRetirementIncomeDifferenceText,

  maxSize,
  filter
}) => {
  return (
    <div className='edited-retirement-budget'>
      <div className='edited-retirement-budget__buckets'>
        <Card noShadow noBorder>
          <CardHeading>
            <h5> Monthly retirement budget </h5>
            {filter ? filter() : null}
          </CardHeading>
          <CardContent>
            <div className='budget-buckets'>
              <MonthlyRetirementBudgetBucket
                income={baseScenarioDesiredRetirementIncome}
                maxSize={maxSize}
                headingText={'Desired retirement budget'}
                additionalClass={'bucket__container--required'}/>

              <MonthlyRetirementBudgetBucket
                income={comparedScenarioActualRetirementIncome}
                maxSize={maxSize}
                headingText={'Projected retirement income'}
                additionalClass={'bucket__container--projected'}/>

              <MonthlyRetirementBudgetBucket
                income={baseScenarioActualRetirementIncome}
                maxSize={maxSize}
                headingText={'Scenario retirement income'}
                additionalClass={'bucket__container--edited'}/>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className='edited-retirement-budget__summary'>
        <div className='summary-items'>
          <BudgetSummary
            className='summary-improved-plan'
            title='Current Plan:'
            formattedDesiredIncome={
              formatNumber(comparedScenarioDesiredRetirementIncome)
            }
            formattedActualIncome={
              formatNumber(comparedScenarioActualRetirementIncome)
            }
            differenceText={comparedScenarioRetirementIncomeDifferenceText}
            formattedDifferenceAmount={
              formatNumber(comparedScenarioRetirementIncomeAbsoluteDifference)
            }/>

          <BudgetSummary
            className='summary-current-plan'
            title='Scenario Plan:'
            formattedDesiredIncome={
              formatNumber(baseScenarioDesiredRetirementIncome)
            }
            formattedActualIncome={
              formatNumber(baseScenarioActualRetirementIncome)
            }
            differenceText={baseScenarioRetirementIncomeDifferenceText}
            formattedDifferenceAmount={
              formatNumber(baseScenarioRetirementIncomeAbsoluteDifference)
            }/>
        </div>
      </div>
    </div>
  )
}

EditedMonthlyRetirementBudget.propTypes = {
  /** Desired monthly retirement income as per the scenario's budget */
  baseScenarioDesiredRetirementIncome: PropTypes.number.isRequired,
  /** Actual monthly retirement income calculated as maximum safe retirement
   * income that can be drawn. */
  baseScenarioActualRetirementIncome: PropTypes.number.isRequired,
  /** Difference between the base scenario's desired and actual retirement income */
  baseScenarioRetirementIncomeAbsoluteDifference: PropTypes.number.isRequired,
  /** Text label describing the difference between the base scenario's desired and actual retirement income */
  baseScenarioRetirementIncomeDifferenceText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]).isRequired,

  /** Current scenario's desired monthly retirement income as per the scenario's
   * budget */
  comparedScenarioDesiredRetirementIncome: PropTypes.number.isRequired,
  /** Current scenario's actual monthly retirement income calculated as maximum
   * safe retirement income that can be drawn. */
  comparedScenarioActualRetirementIncome: PropTypes.number.isRequired,
  /** Difference between the compared scenario's desired and actual retirement income */
  comparedScenarioRetirementIncomeAbsoluteDifference: PropTypes.number.isRequired,
  /** Text label describing the difference between the compared scenario's desired and actual retirement income */
  comparedScenarioRetirementIncomeDifferenceText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]).isRequired,

  /** Maximum income value in a set of buckets to calculate relative percentages */
  maxSize: PropTypes.number.isRequired
}

export { EditedMonthlyRetirementBudget }

const BudgetSummary = ({
  className,
  title,

  formattedDesiredIncome,
  formattedActualIncome,

  differenceText,
  formattedDifferenceAmount
}) => (
  <div className={`summary ${className}`}>
    <div className='summary__title'>
      <p>{title}</p>
    </div>
    <div className='summary__amounts'>
      <ul className=''>
        <li className='desired'>{formattedDesiredIncome}</li>
        <li className='actual'>{formattedActualIncome}</li>
      </ul>
    </div>
    <div className='summary__totals'>
      <p className='totals__text'>
        {differenceText}
      </p>
      <p className='totals__amount'>
        <strong>{formattedDifferenceAmount}</strong>
      </p>
    </div>
  </div>
)

BudgetSummary.propTypes = {
  className: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,

  formattedDesiredIncome: PropTypes.string.isRequired,
  formattedActualIncome: PropTypes.string.isRequired,

  differenceText: PropTypes.oneOfType([ PropTypes.string, PropTypes.node ]).isRequired,
  formattedDifferenceAmount: PropTypes.string.isRequired
}
